// Does not govern Active Directory sign-in. The login page asks the API
// (GET /tenant-settings/public -> adLoginEnabled) which is per tenant.
const IMS_SLUG_NAME = import.meta.env.VITE_IMS_SLUG_NAME ?? "";

// This function runs once per API call, so the complaint below is said once
// per page instead of once per request — a wall of the same line is the thing
// it exists to replace.
let warnedAboutMissingSlug = false;

/**
 * The tenant slug for the current page, sent as `X-Tenant-Slug` on every API
 * call (see lib/api.js).
 *
 * Normally this is the subdomain — `abc.lvh.me` is tenant `abc`. Staff always
 * arrive on a tenant subdomain, so that is all this needed for a long time.
 *
 * Candidate-facing links are the exception, and they are why `?c=` is checked
 * first. A page like the interview RSVP is opened by someone who has never
 * logged in, from a link we put in a WhatsApp message or an email, and it is
 * deliberately served from ONE flat host: the RSVP token resolves the tenant
 * server-side, so there is no wildcard DNS to depend on and no subdomain to
 * splice in (see PUBLIC_LINK_BASE_URL and interview/service.py::_build_rsvp_link,
 * which carries `c=<slug>` on the link "for the frontend").
 *
 * Without that check, the slug came out as the first label of whatever host was
 * serving the page — on a tunnel, `unprecocious-shaunta-unsentient` — and every
 * request from the RSVP page was rejected with TENANT_CONTEXT_MISSING. The
 * candidate saw a page that loaded and then failed, which is the worst possible
 * shape for the one link we ask them to click.
 *
 * Examples:
 * - abc.lvh.me                          -> "abc"
 * - tenant.app.com                      -> "tenant"
 * - any-host/interview-rsvp?c=acme      -> "acme"
 * - localhost / 127.0.0.1               -> VITE_IMS_SLUG_NAME (and a
 *   console error naming it when it is unset)
 *
 * @returns {string} The tenant slug for this page.
 */
export const getTenantSlug = () => {
  // An explicit slug on the URL wins: it is only ever put there by a
  // candidate-facing link, and it is the one thing that is certainly right.
  try {
    const fromLink = new URLSearchParams(window.location.search).get("c");
    if (fromLink && fromLink.trim()) return fromLink.trim();
  } catch {
    // URLSearchParams is unavailable in some embedded webviews — fall through
    // to the hostname rather than breaking every request on the page.
  }

  const hostname = window.location.hostname;

  // Local development has no subdomain to read, so the slug comes from the
  // dev machine's own `.env`. It means nothing in any deployment — every
  // other host answers from its subdomain — which is exactly why an unset
  // one is easy to miss: the slug comes out empty, every call goes out with
  // no tenant, and the server refuses each of them with
  // TENANT_CONTEXT_MISSING. The login page is the worst of it, because it
  // reads /tenant-settings/public to decide which sign-in form to draw. So
  // the cause is named here rather than left to be inferred from the wall of
  // refusals downstream.
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    if (!IMS_SLUG_NAME && !warnedAboutMissingSlug) {
      warnedAboutMissingSlug = true;
      console.error(
        "[tenant] No tenant slug on localhost: set VITE_IMS_SLUG_NAME in frontend_v2/.env to a tenant's slug, " +
          "or open the app on that tenant's subdomain. Every API call will be refused until you do.",
      );
    }
    return IMS_SLUG_NAME;
  }

  return hostname.split(".")[0];
};
